import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MapPin, ImageOff, RefreshCw, Trash2, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Empty } from "@/components/PageHeader";
import { AnnuncioForm } from "@/components/AnnuncioForm";
import { AnnuncioDetail } from "@/components/AnnuncioDetail";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fileUrl, fmtDateTime, prezzoLabel, BOAT_CATEGORIES, ANNUNCIO_CATEGORIE, slug } from "@/lib/api";

const daysLeft = (iso) => Math.ceil((new Date(iso) - new Date()) / 86400000);

const Filter = ({ value, onChange, options, testid, width = "w-40" }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={`${width} bg-white`} data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testid}-option-${slug(v)}`}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

const AnnuncioCard = ({ a, mine, onOpen, onRenew, onDelete }) => {
  const left = daysLeft(a.expires_at);
  return (
    <div className="fade-up bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow flex flex-col" data-testid={`annuncio-card-${a.id}`}>
      <button onClick={() => onOpen(a)} className="relative h-48 bg-slate-100 block w-full" data-testid={`annuncio-open-button-${a.id}`}>
        {a.foto_ids[0] ? <img src={fileUrl(a.foto_ids[0])} alt={a.titolo} className="w-full h-full object-cover" /> : <ImageOff className="h-8 w-8 text-slate-300 mx-auto" />}
        <Badge className={`absolute top-3 left-3 ${a.tipo === "vendo" ? "bg-emerald-600" : "bg-red-600"}`}>{a.tipo.toUpperCase()}</Badge>
        <span className="absolute bottom-3 right-3 bg-[#0B192C] text-white font-heading text-xl px-3 py-1 rounded-md">{prezzoLabel(a.prezzo)}</span>
      </button>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex gap-1.5 mb-2"><Badge variant="outline">{a.categoria}</Badge>{a.categoria_barca && <Badge variant="outline">{a.categoria_barca}</Badge>}</div>
        <h3 className="font-heading text-xl uppercase leading-tight">{a.titolo}</h3>
        {a.luogo && <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="h-3.5 w-3.5" />{a.luogo}</p>}
        {mine && (
          <div className="mt-auto pt-4 space-y-3">
            <p className={`text-xs ${left <= 5 ? "text-red-600 font-semibold" : "text-muted-foreground"}`} data-testid={`annuncio-expiry-${a.id}`}>
              {left > 0 ? `Scade il ${fmtDateTime(a.expires_at)} (${left} gg)` : "Scaduto"}
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => onRenew(a)} data-testid={`annuncio-renew-button-${a.id}`}><RefreshCw className="h-3.5 w-3.5 mr-1" />Rinnova 1 mese</Button>
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => onDelete(a)} data-testid={`annuncio-delete-button-${a.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />Elimina</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function Mercatino() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "miei" ? "miei" : "tutti";
  const [f, setF] = useState({ tipo: "all", categoria: "all", categoria_barca: "all" });
  const [items, setItems] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/conversazioni").then((r) => setUnread(r.data.reduce((s, c) => s + c.unread, 0)));
  }, []);

  const load = useCallback(async () => {
    const p = { mine: tab === "miei" };
    Object.entries(f).forEach(([k, v]) => { if (v !== "all") p[k] = v; });
    const { data } = await api.get("/annunci", { params: p });
    setItems(data);
  }, [f, tab]);

  useEffect(() => {
    load();
  }, [load]);

  const renew = async (a) => {
    try {
      await api.post(`/annunci/${a.id}/rinnova`);
      toast.success("Annuncio rinnovato per un altro mese");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (a) => {
    if (!window.confirm("Eliminare questo annuncio?")) return;
    try {
      await api.delete(`/annunci/${a.id}`);
      toast.success("Annuncio eliminato");
      setDetail(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader kicker="Compro · Vendo" title="Mercatino">
        <Button variant="outline" onClick={() => navigate("/messaggi")} className="rounded-full bg-white" data-testid="mercatino-messaggi-button">
          <MessageCircle className="h-4 w-4 mr-1" />Messaggi{unread > 0 && <span className="ml-2 bg-red-600 text-white text-[11px] font-bold rounded-full px-2" data-testid="mercatino-messaggi-unread">{unread}</span>}
        </Button>
        <Button onClick={() => setFormOpen(true)} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="annuncio-add-button">
          <Plus className="h-4 w-4 mr-1" />Nuovo annuncio
        </Button>
      </PageHeader>
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
        <Tabs value={tab} onValueChange={(v) => setParams(v === "miei" ? { tab: "miei" } : {})}>
          <TabsList>
            <TabsTrigger value="tutti" data-testid="mercatino-tab-tutti">Tutti gli annunci</TabsTrigger>
            <TabsTrigger value="miei" data-testid="mercatino-tab-miei">I miei annunci</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex flex-wrap gap-2 md:ml-auto">
          <Filter value={f.tipo} onChange={(v) => setF({ ...f, tipo: v })} options={[["all", "Compro e vendo"], ["vendo", "Vendo"], ["compro", "Compro"]]} testid="mercatino-filter-tipo" />
          <Filter value={f.categoria} onChange={(v) => setF({ ...f, categoria: v })} options={[["all", "Tutte le categorie"], ...ANNUNCIO_CATEGORIE.map((c) => [c, c])]} testid="mercatino-filter-categoria" width="w-44" />
          <Filter value={f.categoria_barca} onChange={(v) => setF({ ...f, categoria_barca: v })} options={[["all", "Tutte le classi"], ...BOAT_CATEGORIES.map((c) => [c, c])]} testid="mercatino-filter-classe" />
        </div>
      </div>
      {items.length === 0 ? <Empty data-testid="mercatino-empty">Nessun annuncio trovato.</Empty> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="mercatino-list">
          {items.map((a) => <AnnuncioCard key={a.id} a={a} mine={tab === "miei" || a.owner_id === user.id && tab === "miei"} onOpen={setDetail} onRenew={renew} onDelete={remove} />)}
        </div>
      )}
      <AnnuncioForm open={formOpen} onOpenChange={setFormOpen} onSaved={load} />
      <AnnuncioDetail annuncio={detail} onOpenChange={(o) => !o && setDetail(null)} />
    </div>
  );
}
